import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import { ListItemText } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';

import { fundsSlice, publicOfferSlice } from '!views/index';
import { marketStockExchangeSlice } from '!views/Market/StockExchange/_marketStockExchange.slice';
import { menusSlice } from '»components/Menus/_menus.slice';
import {
  getActiveMenuItemSelector,
  getActiveNavigationMenuItemSelector,
  getMainMenu,
  isMenuOpened,
} from '»components/Menus/menus.selectors';
import {
  DrawerMobileStyled,
  FontAwesomeIconMobileStyled,
  LeafListItemButtonMobileStyled,
  LeafListItemTextMobileStyled,
  ListItemIconMobileStyled,
  ListMobileStyled,
  RootListItemButtonMobileStyled,
} from '»components/Menus/Mobile';
import { useIsAbleToProceed } from '»utils/customHooks/useIsAbleToProceed';
import { TrlFormattedMessagePlain } from '»utils/i18n';

export const MarketMenuItemMobile = () => {
  const dispatch = useDispatch();

  const activeMenuItem = useSelector(getActiveMenuItemSelector);
  const activeNavigationMenuItem = useSelector(
    getActiveNavigationMenuItemSelector,
  );
  const isOpen = useSelector(isMenuOpened);
  const mainMenu = useSelector(getMainMenu);

  const { isAbleToProceed } = useIsAbleToProceed();

  const available = mainMenu.some(
    (menuOption) => menuOption.optionId === 'TRLT_MARKET',
  );
  const availablePrimary = mainMenu.some(
    (menuOption) => menuOption.optionId === 'TRLT_MARKET_PRIMARY',
  );
  const availableStockExchange = mainMenu.some(
    (menuOption) => menuOption.optionId === 'TRLT_MARKET_STOCK_EXCHANGE',
  );
  const availableFunds = mainMenu.some(
    (menuOption) => menuOption.optionId === 'TRLT_MARKET_FUNDS',
  );

  const OPTION_ID = 'MARKET';
  const LEAF_OPTION_ID1 = 'PRIMARY';
  const LEAF_OPTION_ID2 = 'BOLSA';
  const LEAF_OPTION_ID3 = 'FUNDS';

  const handleClickLeafMenu = (event, menuItem) => {
    isAbleToProceed(event, () => {
      if (menuItem !== activeMenuItem) {
        dispatch(
          menusSlice.actions.setActiveMenuItem({ activeMenuItem: menuItem }),
        );
        dispatch(
          menusSlice.actions.setActiveNavigationMenuItem({
            activeNavigationMenuItem: '',
          }),
        );

        if (menuItem.includes('BOLSA')) {
          dispatch(marketStockExchangeSlice.actions.setCurrentTab(0));
        } else if (menuItem.includes('PRIMARY')) {
          dispatch(publicOfferSlice.actions.setCurrentTab(0));
        } else {
          dispatch(fundsSlice.actions.setCurrentTab(0));
        }
      }
    });
  };
  const handleClickMenuRoot = (menuItem) => {
    if (activeNavigationMenuItem === menuItem) {
      dispatch(
        menusSlice.actions.setActiveNavigationMenuItem({
          activeNavigationMenuItem: '',
        }),
      );
    } else {
      dispatch(
        menusSlice.actions.setActiveNavigationMenuItem({
          activeNavigationMenuItem: menuItem,
        }),
      );
    }
  };

  const getMenuLabel = () => {
    let menuLabelExpression = 'TRLT_UI-MARKET';

    if (
      activeMenuItem === 'MARKET:BOLSA' ||
      activeMenuItem === 'MARKET:BOLSA:DETAILS'
    ) {
      menuLabelExpression = 'TRLT_UI-EXCHANGE';
    } else if (activeMenuItem === 'MARKET:PRIMARY') {
      menuLabelExpression = 'TRLT_UI-PRIMARY';
    } else if (
      activeMenuItem === 'MARKET:FUNDS' ||
      activeMenuItem === 'MARKET:FUNDS:DETAILS'
    ) {
      menuLabelExpression = 'TRLT_UI-FUNDS';
    }

    return TrlFormattedMessagePlain({ id: menuLabelExpression });
  };

  // eslint-disable-next-line no-undef
  const myConfig = config();
  const subMenuTimeout = myConfig.subMenuTimeout ?? null;

  useEffect(() => {
    if (subMenuTimeout === null) {
      return;
    }
    const timer = setTimeout(() => {
      dispatch(
        menusSlice.actions.setActiveNavigationMenuItem({
          activeNavigationMenuItem: '',
        }),
      );
    }, subMenuTimeout);

    return () => clearTimeout(timer);
  }, [activeNavigationMenuItem, dispatch, subMenuTimeout]);

  if (!available) return null;

  return (
    <>
      <DrawerMobileStyled
        open={activeNavigationMenuItem.includes(OPTION_ID)}
        anchor="bottom"
        onClose={(_, reason) =>
          (reason === 'backdropClick' || reason === undefined) &&
          dispatch(
            menusSlice.actions.setActiveNavigationMenuItem({
              activeNavigationMenuItem: '',
            }),
          )
        }
      >
        <ListMobileStyled
          component="div"
          disablePadding
          opened={activeNavigationMenuItem.includes(OPTION_ID)}
          selected={activeMenuItem?.includes(OPTION_ID)}
        >
          {availableStockExchange && (
            <LeafListItemButtonMobileStyled
              id="btnMenuMarketSecondary"
              onClick={(event) =>
                handleClickLeafMenu(event, `${OPTION_ID}:${LEAF_OPTION_ID2}`)
              }
              component={Link}
              to="/Market/StockExchange"
              selected={activeMenuItem?.includes(
                `${OPTION_ID}:${LEAF_OPTION_ID2}`,
              )}
            >
              <LeafListItemTextMobileStyled
                primary={<FormattedMessage id="TRLT_UI-EXCHANGE" />}
                selected={activeMenuItem?.includes(
                  `${OPTION_ID}:${LEAF_OPTION_ID2}`,
                )}
                isOpen={isOpen}
              />
            </LeafListItemButtonMobileStyled>
          )}
          {availablePrimary && (
            <LeafListItemButtonMobileStyled
              onClick={(event) =>
                handleClickLeafMenu(event, `${OPTION_ID}:${LEAF_OPTION_ID1}`)
              }
              component={Link}
              to="/Market/Primary"
              selected={activeMenuItem?.includes(
                `${OPTION_ID}:${LEAF_OPTION_ID1}`,
              )}
              id="btnMenuMarketPrimary"
            >
              <LeafListItemTextMobileStyled
                primary={<FormattedMessage id="TRLT_UI-PRIMARY" />}
                selected={activeMenuItem?.includes(
                  `${OPTION_ID}:${LEAF_OPTION_ID1}`,
                )}
                isOpen={isOpen}
              />
            </LeafListItemButtonMobileStyled>
          )}
          {availableFunds && (
            <LeafListItemButtonMobileStyled
              onClick={(event) =>
                handleClickLeafMenu(event, `${OPTION_ID}:${LEAF_OPTION_ID3}`)
              }
              component={Link}
              to="/Market/Funds"
              selected={activeMenuItem?.includes(
                `${OPTION_ID}:${LEAF_OPTION_ID3}`,
              )}
              id="btnMenuMarketFunds"
            >
              <LeafListItemTextMobileStyled
                primary={<FormattedMessage id="TRLT_UI-FUNDS" />}
                selected={activeMenuItem?.includes(
                  `${OPTION_ID}:${LEAF_OPTION_ID3}`,
                )}
                isOpen={isOpen}
              />
            </LeafListItemButtonMobileStyled>
          )}
        </ListMobileStyled>
      </DrawerMobileStyled>
      <RootListItemButtonMobileStyled
        onClick={() => handleClickMenuRoot(OPTION_ID)}
        opened={activeNavigationMenuItem.includes(OPTION_ID)}
        selected={activeMenuItem?.includes(OPTION_ID)}
        isSubMenuPopup={activeNavigationMenuItem !== ''}
        id="btnMenuMarket"
      >
        <ListItemIconMobileStyled>
          <FontAwesomeIconMobileStyled
            icon={faChartLine}
            selected={activeMenuItem?.includes(OPTION_ID)}
          />
        </ListItemIconMobileStyled>
        <ListItemText primary={getMenuLabel()} />
      </RootListItemButtonMobileStyled>
    </>
  );
};

MarketMenuItemMobile.propTypes = {
  isOpen: PropTypes.bool,
  activeMenuItem: PropTypes.string,
  available: PropTypes.bool,
  availablePrimary: PropTypes.bool,
  availableStockExchange: PropTypes.bool,
  availableFunds: PropTypes.bool,
  setActiveMenuItem: PropTypes.func,
  activeNavigationMenuItem: PropTypes.string,
  setActiveNavigationMenuItem: PropTypes.func,
};
